import React, { useEffect, useRef, useState } from 'react';
import { IconBook, IconCards, IconCode, IconList, IconTarget, IconX } from './icons';
import { loadPrefs } from './settingsPrefs';

/*
 * PracticeChooser — "Practice this": pick HOW to practise a piece of the hub,
 * then hand off to the existing practice screens (no separate practice
 * system). Only options the modes actually honour are shown: difficulty and
 * session size for MCQ / Theory; Flashcards and Case Study take neither.
 * Coding appears only for DSA content.
 */
const MODES = [
  { id: 'mcq', label: 'MCQ', note: 'Multiple-choice questions to test your understanding', Icon: IconList, opts: true },
  { id: 'theory', label: 'Theory', note: 'Explain concepts in your own words, with a tutor', Icon: IconBook, opts: true },
  { id: 'flashcards', label: 'Flashcards', note: 'Quick revision with key concepts', Icon: IconCards },
  { id: 'case', label: 'Case Study', note: 'Apply it to a real-world scenario', Icon: IconTarget },
];
const DIFFICULTY = [[null, 'Adaptive'], ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']];
const SIZES = [[3, 'Quick · 3'], [5, 'Standard · 5'], [10, 'Deep · 10']];

const PracticeChooser = ({ title, subjects, initialMode = 'mcq', coding = false, onStart, onOpenCoding, onClose }) => {
  // Settings → Practice defaults: the generic 'mcq' start follows the learner's default
  // mode; an explicit other mode (e.g. Case Study) is kept.
  const [prefs] = useState(loadPrefs);
  const [mode, setMode] = useState(() => (initialMode === 'mcq' && MODES.some((m) => m.id === prefs.practiceMode) ? prefs.practiceMode : initialMode));
  const [difficulty, setDifficulty] = useState(prefs.difficulty || null);
  const [size, setSize] = useState([3, 5, 10].includes(prefs.sessionSize) ? prefs.sessionSize : 5);
  const dialogRef = useRef(null);
  const showOpts = MODES.find((m) => m.id === mode)?.opts;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.querySelector('.kh-mode.on')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="kh-modal-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="kh-modal" role="dialog" aria-modal="true" aria-labelledby="kh-practice-title" ref={dialogRef}>
        <div className="kh-modal-head">
          <div>
            <h2 id="kh-practice-title">Practice: {title}</h2>
            <p>Choose how you want to practise this.</p>
          </div>
          <button type="button" className="kh-icon-btn" onClick={onClose} aria-label="Close"><IconX size={18} /></button>
        </div>
        <div className="kh-modes" role="radiogroup" aria-label="Practice mode">
          {MODES.map(({ id, label, note, Icon }) => (
            <button key={id} type="button" role="radio" aria-checked={mode === id}
              className={`kh-mode m-${id}${mode === id ? ' on' : ''}`} onClick={() => setMode(id)}>
              <span className="kh-mode-icon" aria-hidden="true"><Icon size={18} /></span>
              <span><b>{label}</b><small>{note}</small></span>
            </button>
          ))}
          {coding && (
            <button type="button" className="kh-mode m-coding" onClick={onOpenCoding}>
              <span className="kh-mode-icon" aria-hidden="true"><IconCode size={18} /></span>
              <span><b>Coding</b><small>Solve problems in the DSA workspace</small></span>
            </button>
          )}
        </div>
        {showOpts && (
          <div className="kh-modal-opts">
            <p className="kh-opts-h">Customize <span>(optional)</span></p>
            <label>
              <span>Difficulty</span>
              <select value={difficulty ?? ''} onChange={(e) => setDifficulty(e.target.value || null)}>
                {DIFFICULTY.map(([v, l]) => <option key={l} value={v ?? ''}>{l}</option>)}
              </select>
            </label>
            <label>
              <span>Number of questions</span>
              <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
                {SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
        )}
        <div className="kh-modal-foot">
          <button type="button" className="pu-btn pu-btn-quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="pu-btn pu-btn-primary"
            onClick={() => onStart({ mode, subjects, size: showOpts ? size : 5, difficulty: showOpts ? difficulty : null })}>
            Start practice
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeChooser;
